import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Paper,
  Container,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Fade,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Timer as TimerIcon,
  Quiz as QuizIcon,
  NavigateBefore,
  NavigateNext,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon,
  ExitToApp as ExitIcon
} from '@mui/icons-material';
import QuestionCard from './components/QuestionCard';
import Timer from './components/Timer';
import ResultsGrid from './components/ResultsGrid';
import ExplanationDialog from './components/ExplanationDialog';
import { useAuth } from '../../context/AuthContext';

// URL의 쿼리 파라미터를 파싱하는 헬퍼 훅
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// 1문제당 108초 (100문제 3시간 기준)
const SECONDS_PER_QUESTION = 108;

export default function TestPage() {
  const query = useQuery();
  const navigate = useNavigate();
  const { user } = useAuth();

  const mode = query.get('mode'); // 'test' or 'study'
  const setId = query.get('setId');
  const subjectId = query.get('subjectId');

  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [userAnswers, setUserAnswers] = useState({}); // { questionId: selectedChoice }
  const [isLoading, setIsLoading] = useState(true);
  
  // Test Mode State
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Study Mode State
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [questionFilter, setQuestionFilter] = useState('all'); // 'all', 'correct', 'incorrect', 'unanswered'

  // Explanation Dialog State
  const [explanationDialogOpen, setExplanationDialogOpen] = useState(false);
  const [selectedQuestionForExp, setSelectedQuestionForExp] = useState(null);

  // Exit confirmation dialog state
  const [exitDialogOpen, setExitDialogOpen] = useState(false);

  // 데이터 불러오기
  useEffect(() => {
    const fetchQuestions = async () => {
      setIsLoading(true);
      let queryBuilder = supabase.from('mbe_questions').select('*');

      if (mode === 'test' && setId) {
        queryBuilder = queryBuilder.eq('set_id', Number(setId));
      } else if (mode === 'study' && subjectId) {
        // 쉼표로 구분된 ID를 배열로 만들고 .in() 필터를 사용
        const subjectIds = subjectId.split(',');
        queryBuilder = queryBuilder.in('subject_id', subjectIds);
      } else {
        setIsLoading(false);
        return; // 유효하지 않은 파라미터
      }

      const { data, error } = await queryBuilder.order('id');
      
      if (error) {
        console.error('Error fetching questions:', error);
        setQuestions([]);
      } else {
        setQuestions(data);
        if (mode === 'test' && data && data.length > 0) { // 데이터가 있을 때만 시간 설정
          setTimeLeft(data.length * SECONDS_PER_QUESTION);
        }
      }
      setIsLoading(false);
    };

    fetchQuestions();
  }, [mode, setId, subjectId]);

  const handleSubmit = useCallback(async () => {
    if (!user || !setId) return;
    
    setIsSubmitted(true);
    
    const finalScore = questions.filter(q => userAnswers[q.id] === q.correct_choice).length;
    setScore(finalScore);

    const { error } = await supabase.from('mbe_answers').insert({
        user_id: user.id,
        set_id: Number(setId),
        answers: userAnswers,
        score: finalScore,
        submission_time: new Date().toISOString(),
    });

    if (error) {
        console.error('Failed to submit answers:', error);
        alert('답안 제출에 실패했습니다.');
    }
  }, [user, setId, questions, userAnswers]);

  // 타이머 로직 (시험 모드 전용)
  useEffect(() => {
    // 로딩 중이거나, 테스트 모드가 아니거나, 이미 제출된 상태에서는 타이머를 실행하지 않음
    if (isLoading || mode !== 'test' || isSubmitted) {
      return;
    }
    
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }

    const interval = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timeLeft, isSubmitted, mode, isLoading, handleSubmit]);


  const handleAnswerSelect = (questionId, choice) => {
    setUserAnswers(prev => ({ ...prev, [questionId]: choice }));
  };

  const handleShowExplanationClick = (question) => {
    setSelectedQuestionForExp(question);
    setExplanationDialogOpen(true);
  };
  
  const handleCloseExplanationDialog = () => {
    setExplanationDialogOpen(false);
    setSelectedQuestionForExp(null);
  };

  const handleExitConfirm = () => {
    navigate('/mbe_test');
  };

  const handleExitCancel = () => {
    setExitDialogOpen(false);
  };

  const handleExitClick = () => {
    // 답변이 있거나 시험 모드인 경우 확인 대화상자 표시
    if (mode === 'test' && Object.keys(userAnswers).length > 0 && !isSubmitted) {
      setExitDialogOpen(true);
    } else {
      navigate('/mbe_test');
    }
  };

  const handleQuestionSelect = (index) => {
    setCurrentIdx(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateQuestion = (direction) => {
    setShowExplanation(false);
    if (direction === 'next' && currentIdx < questions.length - 1) {
        setCurrentIdx(prev => prev + 1);
    } else if (direction === 'prev' && currentIdx > 0) {
        setCurrentIdx(prev => prev - 1);
    }
    // 스터디 모드에서 문제 이동 시 스크롤을 맨 위로
    if (mode === 'study') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // 진행률 계산
  const progress = questions.length > 0 ? ((currentIdx + 1) / questions.length) * 100 : 0;
  const answeredCount = Object.keys(userAnswers).length;



  // 필터 변경 핸들러
  const handleFilterChange = (filter) => {
    setQuestionFilter(filter);
    
    // 필터 변경 시 해당 조건에 맞는 첫 번째 문제로 이동
    if (filter !== 'all') {
      const firstMatchingIndex = questions.findIndex((question) => {
        const isAnswered = !!userAnswers[question.id];
        const isCorrect = userAnswers[question.id] === question.correct_choice;
        
        switch (filter) {
          case 'correct':
            return isAnswered && isCorrect;
          case 'incorrect':
            return isAnswered && !isCorrect;
          case 'unanswered':
            return !isAnswered;
          default:
            return false;
        }
      });
      
      if (firstMatchingIndex !== -1) {
        setCurrentIdx(firstMatchingIndex);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3, boxShadow: 4 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            문제를 불러오는 중입니다...
          </Typography>
        </Card>
      </Box>
    );
  }

  if (!questions.length) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3, boxShadow: 4 }}>
          <Typography variant="h6" gutterBottom>문제를 찾을 수 없습니다.</Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/mbe_test')} 
            sx={{ 
              mt: 2,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
              }
            }}
          >
            선택 페이지로 돌아가기
          </Button>
        </Card>
      </Box>
    );
  }

  const currentQuestion = questions[currentIdx];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 3
      }}
    >
      <Container maxWidth="lg">
        {/* 헤더 영역 */}
        <Fade in timeout={800}>
          <Card 
            sx={{ 
              mb: 3, 
              borderRadius: 3, 
              boxShadow: 4,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <QuizIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                      {mode === 'test' ? '🎯 실전 테스트' : '📚 스터디 모드'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {mode === 'test' ? `총 ${questions.length}문제` : '학습 모드'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* 메인으로 돌아가기 버튼 */}
                                      <Button
                      variant="outlined"
                      startIcon={<HomeIcon />}
                      onClick={handleExitClick}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontWeight: 'bold',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      메인으로
                    </Button>
                  
                  {mode === 'test' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TimerIcon sx={{ color: timeLeft < 600 ? 'error.main' : 'text.secondary' }} />
                      <Typography 
                        variant="h6" 
                        fontWeight="bold"
                        sx={{ 
                          color: timeLeft < 600 ? 'error.main' : 'text.primary',
                          fontFamily: 'monospace'
                        }}
                      >
                        <Timer seconds={timeLeft} />
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* 진행률 표시 */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    문제 {currentIdx + 1} / {questions.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    답변 완료: {answeredCount}문제
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={progress} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                      borderRadius: 4
                    }
                  }} 
                />
              </Box>

              {/* 상태 칩들 */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${progress.toFixed(1)}% 완료`}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
                {mode === 'test' && (
                  <Chip 
                    label={timeLeft < 600 ? '⚠️ 시간 부족' : '⏰ 충분한 시간'}
                    color={timeLeft < 600 ? 'error' : 'success'}
                    size="small"
                  />
                )}
                {userAnswers[currentQuestion.id] && (
                  <Chip 
                    label="✅ 답변 완료"
                    color="success"
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* 문제 카드 */}
        <Slide direction="up" in timeout={600}>
          <Box>
            <QuestionCard
              question={currentQuestion}
              userAnswer={userAnswers[currentQuestion.id]}
              correctAnswer={currentQuestion.correct_choice}
              onAnswerSelect={handleAnswerSelect}
              isStudyMode={mode === 'study'}
              isSubmitted={isSubmitted}
              onShowExplanationClick={handleShowExplanationClick}
            />
          </Box>
        </Slide>

        {/* 네비게이션 버튼 */}
        <Fade in timeout={1000}>
          <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button 
                  variant="outlined" 
                  onClick={() => navigateQuestion('prev')}
                  disabled={currentIdx === 0}
                  startIcon={<NavigateBefore />}
                  sx={{ 
                    px: 3,
                    borderRadius: 2,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  이전 문제
                </Button>
                
                <Typography 
                  variant="h6" 
                  color="text.secondary"
                  sx={{ fontWeight: 'bold' }}
                >
                  {currentIdx + 1} / {questions.length}
                </Typography>
                
                <Button 
                  variant="outlined" 
                  onClick={() => navigateQuestion('next')}
                  disabled={currentIdx === questions.length - 1}
                  endIcon={<NavigateNext />}
                  sx={{ 
                    px: 3,
                    borderRadius: 2,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  다음 문제
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* 스터디 모드 전용: 문제 네비게이션 타일 */}
        {mode === 'study' && (
          <Fade in timeout={1200}>
            <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  📋 문제 네비게이션
                </Typography>
                
                {/* 필터 버튼들 */}
                <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`전체 (${questions.length})`}
                    variant={questionFilter === 'all' ? 'filled' : 'outlined'}
                    color={questionFilter === 'all' ? 'primary' : 'default'}
                    size="small"
                    onClick={() => handleFilterChange('all')}
                    sx={{ 
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': { transform: 'scale(1.05)' }
                    }}
                  />
                  <Chip 
                    label={`정답 (${questions.filter(q => userAnswers[q.id] === q.correct_choice).length})`}
                    color="success"
                    variant={questionFilter === 'correct' ? 'filled' : 'outlined'}
                    size="small"
                    onClick={() => handleFilterChange('correct')}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { transform: 'scale(1.05)' }
                    }}
                  />
                  <Chip 
                    label={`오답 (${questions.filter(q => userAnswers[q.id] && userAnswers[q.id] !== q.correct_choice).length})`}
                    color="error"
                    variant={questionFilter === 'incorrect' ? 'filled' : 'outlined'}
                    size="small"
                    onClick={() => handleFilterChange('incorrect')}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { transform: 'scale(1.05)' }
                    }}
                  />
                  <Chip 
                    label={`미제출 (${questions.filter(q => !userAnswers[q.id]).length})`}
                    color="default"
                    variant={questionFilter === 'unanswered' ? 'filled' : 'outlined'}
                    size="small"
                    onClick={() => handleFilterChange('unanswered')}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { transform: 'scale(1.05)' }
                    }}
                  />
                </Box>

                {/* 문제 번호 타일들 */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))', 
                  gap: 1,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'grey.50'
                }}>
                  {questions.map((question, index) => {
                    const isAnswered = !!userAnswers[question.id];
                    const isCorrect = userAnswers[question.id] === question.correct_choice;
                    const isCurrent = index === currentIdx;
                    
                    // 필터링된 문제가 아닌 경우 숨김
                    const shouldShow = questionFilter === 'all' || 
                      (questionFilter === 'correct' && isAnswered && isCorrect) ||
                      (questionFilter === 'incorrect' && isAnswered && !isCorrect) ||
                      (questionFilter === 'unanswered' && !isAnswered);
                    
                    if (!shouldShow) return null;
                    
                    let chipColor = 'default';
                    let chipVariant = 'outlined';
                    let chipIcon = '';
                    
                    if (isCurrent) {
                      chipColor = 'primary';
                      chipVariant = 'filled';
                    } else if (isAnswered) {
                      if (isCorrect) {
                        chipColor = 'success';
                        chipVariant = 'filled';
                        chipIcon = '✓';
                      } else {
                        chipColor = 'error'; 
                        chipVariant = 'filled';
                        chipIcon = '✗';
                      }
                    }
                    
                    return (
                      <Chip
                        key={question.id}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {chipIcon && <span style={{ fontSize: '12px' }}>{chipIcon}</span>}
                            <span>{index + 1}</span>
                          </Box>
                        }
                        color={chipColor}
                        variant={chipVariant}
                        onClick={() => handleQuestionSelect(index)}
                        sx={{
                          minWidth: '45px',
                          height: '32px',
                          cursor: 'pointer',
                          fontWeight: isCurrent ? 'bold' : 'normal',
                          fontSize: '0.875rem',
                          border: isCurrent ? '2px solid' : '1px solid',
                          borderColor: isCurrent ? 'primary.main' : 'divider',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: 2,
                            zIndex: 1
                          },
                          transition: 'all 0.2s ease'
                        }}
                      />
                    );
                  })}
                </Box>

                {/* 범례 */}
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: 'success.main', borderRadius: '50%' }} />
                    <Typography variant="caption">정답</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: 'error.main', borderRadius: '50%' }} />
                    <Typography variant="caption">오답</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: 'grey.400', borderRadius: '50%' }} />
                    <Typography variant="caption">미제출</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: 'primary.main', borderRadius: '50%' }} />
                    <Typography variant="caption">현재 문제</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        )}

        {/* 제출 버튼 */}
        {mode === 'test' && !isSubmitted && (
          <Fade in timeout={1200}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleSubmit}
                startIcon={<SendIcon />}
                sx={{ 
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 3,
                  background: 'linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)',
                  boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FF5252 30%, #FF7043 90%)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 40px rgba(255, 107, 107, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                답안 최종 제출
              </Button>
            </Box>
          </Fade>
        )}

        {/* 결과 표시 */}
        {isSubmitted && (
          <Fade in timeout={800}>
            <Box sx={{ mt: 4 }}>
              <Card 
                sx={{ 
                  mb: 3, 
                  borderRadius: 3, 
                  boxShadow: 4,
                  background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
                  color: 'white'
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <CheckCircleIcon sx={{ fontSize: 64, mb: 2 }} />
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    시험이 완료되었습니다! 🎉
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    총 {questions.length}문제 중 {score}문제를 맞혔습니다.
                  </Typography>
                  <Typography variant="h6">
                    정답률: {((score / questions.length) * 100).toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
              
              <ResultsGrid
                questions={questions}
                userAnswers={userAnswers}
                onQuestionSelect={handleQuestionSelect}
              />
            </Box>
          </Fade>
        )}

                 <ExplanationDialog
           open={explanationDialogOpen}
           onClose={handleCloseExplanationDialog}
           question={selectedQuestionForExp}
         />

         {/* Exit Confirmation Dialog */}
         <Dialog
           open={exitDialogOpen}
           onClose={handleExitCancel}
           maxWidth="sm"
           fullWidth
           PaperProps={{
             sx: {
               borderRadius: 3,
               boxShadow: 4
             }
           }}
         >
           <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
             <ExitIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
             <Typography variant="h5" fontWeight="bold">
               시험을 종료하시겠습니까?
             </Typography>
           </DialogTitle>
           <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
             <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
               현재까지 작성한 답안이 저장되지 않습니다.
             </Typography>
             <Typography variant="body2" color="error.main" fontWeight="bold">
               ⚠️ 이 작업은 되돌릴 수 없습니다.
             </Typography>
           </DialogContent>
           <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
             <Button
               variant="outlined"
               onClick={handleExitCancel}
               sx={{
                 px: 4,
                 py: 1,
                 borderRadius: 2,
                 fontWeight: 'bold'
               }}
             >
               계속 진행
             </Button>
             <Button
               variant="contained"
               onClick={handleExitConfirm}
               color="error"
               sx={{
                 px: 4,
                 py: 1,
                 borderRadius: 2,
                 fontWeight: 'bold'
               }}
             >
               나가기
             </Button>
           </DialogActions>
         </Dialog>
       </Container>
     </Box>
   );
 }
